import {
  Dialog,
  Box,
  Typography,
  Button,
  useMediaQuery,
  Divider,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material'
import { FC, useEffect } from 'react'
import { appTheme } from '../../theme'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { IconCustom } from '../IconCustom/IconCustom'
import { BadgeEventFlag } from '../BadgeEventFlag/BadgeEventFlag'
import { useEvent } from '../../hooks/useEvent';
import { usePermissions } from '../../hooks/usePermissions';
import { Event } from '../../services/Types';

export interface EventDetailsPopupProps {
  id: number
  setOpen: (open: boolean) => void
  open: boolean
}

export const EventDetailsPopup: FC<EventDetailsPopupProps> = ({
  id,
  setOpen,
  open
}) => {
  const fullScreen = useMediaQuery(appTheme.breakpoints.down('md'))
  const { getEventData, eventData, getEventRoomBookingFormatted } = useEvent()
  const { canUserCancel, canUserViewEvent } = usePermissions(eventData || {} as Event)

  useEffect(() => {
    getEventData(id)
  }, [id])
  const formattedBookingDates = getEventRoomBookingFormatted()

  return (
    <Dialog fullScreen={fullScreen} open={open} onClose={() => setOpen(false)}
      sx={{
        '& .MuiDialog-paper': {
          width: '706px',
          maxHeight: '578px',
          borderRadius: '12px',
          py: '10px',
          px: '10px',
          gap: '16px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          boxShadow: '0px 2px 20px rgba(0, 0, 0, 0.2)',
          position: 'relative'
        }
      }}
    >
      <Box sx={{ backgroundColor: '#F5F7FA', px: '20px', py: '20px', borderRadius: '12px' }}>
        <Box sx={{ flex: 1, overflow: 'auto', pr: '16px' }}>
          <Grid container>
            <Grid item xs={1} md={1}>
              <IconCustom type={eventData?.eventType || ''} size={20} />
            </Grid>
            <Grid item xs={11} md={11}>
              <Typography variant='h2' sx= {{ margin: '0px' }}>
                {eventData?.title}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: '80px', mt: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <BadgeEventFlag label='Dry Run' />
                  <Typography variant='h4' sx= {{ margin: '0px', textAlign: 'center' }}>
                    {formattedBookingDates?.dry?.startEndDate || 'N/A'}
                  </Typography>
                  <Typography variant='body2' sx= {{ margin: '0px', textAlign: 'center' }}>
                    {formattedBookingDates?.dry?.startEndTime || 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <BadgeEventFlag label='Live Event' />
                  <Typography variant='h4' sx= {{ margin: '0px', textAlign: 'center' }}>
                    {formattedBookingDates?.live?.startEndDate}
                  </Typography>
                  <Typography variant='body2' sx= {{ margin: '0px', textAlign: 'center' }}>
                    {formattedBookingDates?.live?.startEndTime}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>


          <Typography variant='h5'>
            Summary
          </Typography>
          <Typography variant='body1'>
            {eventData?.summary}
          </Typography>

          <Divider />
          <Accordion sx={{ position: 'static', backgroundColor: '#F5F7FA' }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Typography variant='h5'>
                What to Expect for a Dry Run
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant='body1'>
                The WorkforceGPS Events team will conduct a Dry Run for each webinar (not required for webcasts). Attendance by moderators, presenters, and support staff is required.
              </Typography>
              <Typography variant='body1' sx={{ mt: 1 }}>
                During a Dry Run, the team will:
              </Typography>
              <ul>
                <li>Provide an overview of the event room and its features</li>
                <li>Answer any technical or logistical questions</li>
                <li>Time permitting, allow for an overview of your presentation</li>
              </ul>
              <Typography variant='body1' sx={{ mt: 1 }}>
                To learn more, visit our <a href='/support' target='_blank' rel='noopener noreferrer'>Support</a> page.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={{ position: 'static', backgroundColor: '#F5F7FA' }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel2-content"
              id="panel2-header"
            >
              <Typography variant='h5'>
                What to Expect for a Live Event
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant='body1'>
                A pre-call for each webinar will begin 30 minutes before the scheduled start time (not applicable for webcasts).
              </Typography>
              <Typography variant='body1' sx={{ mt: 1 }}>
                During the pre-call, the team will:
              </Typography>
              <ul>
                <li>Identify and resolve potential technology problems</li>
                <li>Confirm final event details</li>
                <li>Address any last-minute questions or issues</li>
              </ul>
              <Typography variant='body1' sx={{ mt: 1 }}>
                All key individuals involved in the event (moderators, presenters, and support staff) are required to attend the pre-call.
              </Typography>
              <Typography variant='body1' sx={{ mt: 1 }}>
                To learn more, visit our <a href='/support' target='_blank' rel='noopener noreferrer'>Support</a> page.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>

        <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '16px',
          justifyContent: 'flex-end',
          borderTop: '1px solid #E8EBF4',
          pt: '16px',
          mt: '16px',
          pr: '20px'
        }}>
          {canUserCancel && <Button variant='outlined' href={`/event/review/${id}`}>
            Cancel request
          </Button>}
          {canUserViewEvent && <Button variant='contained' href={`/event/review/${id}`}>
            View or edit
          </Button>}
        </Box>
      </Box>
    </Dialog>
  );
}
